import { Dispatch } from "@reduxjs/toolkit";
import {
  MessageType,
  MessageTypeEnum,
  setMessage,
} from "../../redux/reducers/messageReducer";
import { OptionType, SubOptionType } from "./types";
import { TFunction } from "i18next";

export const isThisArray = (possibleArray: any) => {
  if (possibleArray !== undefined && Array.isArray(possibleArray)) {
    return true;
  }
  return false;
};

export const isThisArrayEmpty = (possibleArray: any) => {
  if (isThisArray(possibleArray) && possibleArray.length === 0) {
    return true;
  }
  return false;
};

export const isThisArrayNotEmpty = (possibleArray: any) => {
  if (isThisArray(possibleArray) && possibleArray.length > 0) {
    return true;
  }
  return false;
};

export const isThisArrayThisLength = (possibleArray: any, length?: number) => {
  if (
    isThisArray(possibleArray) &&
    length !== undefined &&
    possibleArray.length === length
  ) {
    return true;
  }
  return false;
};

export const ObjectFromArray = (possibleArray: any) => {
  if (isThisArrayNotEmpty(possibleArray)) {
    const obj = possibleArray.reduce((acc: any, current: any) => {
      const [key, value] = Object.entries(current)[0];
      acc[key] = value;
      return acc;
    }, {});
    if (obj !== undefined) {
      return obj;
    }
    return undefined;
  }
};

export const scrollToElement = (
  wrapperId: string,
  scrollToElementId: string,
  behavior?: ScrollBehavior,
) => {
  const scrollToElement = document.getElementById(scrollToElementId);
  const wrapper = document.getElementById(wrapperId);
  if (scrollToElement && wrapper) {
    const newOffset = scrollToElement.offsetTop - 350;
    wrapper.scrollTo({ top: newOffset, behavior: behavior });
  }
};

export const getElementOffsetFromTop = (elementId: string) => {
  const element = document.getElementById(elementId);
  if (element) {
    return element.offsetTop;
  }
};

export const scrollElementToTop = (
  wrapperId: string,
  behavior?: ScrollBehavior,
) => {
  const wrapper = document.getElementById(wrapperId);
  if (wrapper) {
    wrapper.scrollTo({ top: 0, behavior: behavior });
  }
};

export const scrollElementToBottom = (
  wrapperId: string,
  behavior?: ScrollBehavior,
) => {
  const wrapper = document.getElementById(wrapperId);
  if (wrapper) {
    wrapper.scrollTo({ top: wrapper.scrollHeight, behavior: behavior });
  }
};

export const scrollIntoViewIfNeeded = (
  element: HTMLElement | null,
  behavior: ScrollBehavior = "smooth",
): void => {
  if (element) {
    const rect = element.getBoundingClientRect();
    const viewHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const viewWidth = window.innerWidth || document.documentElement.clientWidth;

    const isInView =
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= viewHeight &&
      rect.right <= viewWidth;

    if (!isInView) {
      element.scrollIntoView({
        behavior,
        block: "center",
        inline: "nearest",
      });
    }
  }
};

export const handleCopy = (dispatch: Dispatch, textToCopy?: string) => {
  sendToasterMessage(dispatch, "Text bol úspešne skopírovaný");
  navigator.clipboard.writeText(textToCopy ?? "");
};

export const sendToasterMessage = (
  dispatch: Dispatch,
  text: string,
  type?: MessageTypeEnum,
) => {
  const message: MessageType = {
    type: type ?? MessageTypeEnum.Success,
    display: "toaster",
    description: text,
  };
  dispatch(setMessage(message));
};

export const getMaxPosition = (items: any[]) => {
  if (isThisArrayNotEmpty(items)) {
    const max = items.reduce(
      (max, item) => (item.position > max ? item.position : max),
      -Infinity,
    );
    return max === -Infinity ? 0 : max;
  }
  return 0;
};

export const isDefined = (value: any) => {
  if (value !== undefined && value !== null) {
    return true;
  }
  return false;
};

interface WithValue {
  value: string;
}

export const checkForDuplacateValues = <T extends WithValue>(
  newOption: T,
  options: T[],
) => {
  return options.find((option) => option.value === newOption.value);
};

export const getUserInitials = (name: string) => {
  const splitName = name?.split(" ");
  if (splitName === undefined || splitName.length === 0) {
    return "U";
  }
  const firstLetterOfLastName = splitName[1]?.charAt(0);
  const firstLetterOfFirstName = splitName[splitName.length - 1]?.charAt(0);
  return firstLetterOfLastName + firstLetterOfFirstName;
};

export const getUserTypeDisplayName = (type: string, t: TFunction) => {
  switch (type) {
    case "vseobecny":
      return t("generalDoctor");
    case "vseobecnyCZ":
      return `${t("generalDoctor")} - CZ`;
    case "pediatria":
      return t("pediatric");
    default:
      return t("emptyInProfile");
  }
};

export const isThisOptionDefaultValue = (
  currentOption: OptionType,
  options: OptionType[],
  defaultValue?: string,
) => {
  if (defaultValue?.includes("|")) {
    const defaultValueArray = defaultValue.split("|");
    if (defaultValueArray.includes(currentOption.value)) {
      return true;
    }
    return false;
  }
  const position = currentOption.position;
  const oldOption = options.find((value) => value.position === position);

  if (oldOption?.value === defaultValue) {
    return true;
  }
  return false;
};

export const getHighestNumberInOptionValues = (
  options: OptionType[],
  t: TFunction,
) => {
  if (isThisArrayNotEmpty(options)) {
    const highestNumber = options.reduce((highestNumber, option) => {
      if (option.name.startsWith(t("newOption"))) {
        const number = parseInt(option.value.charAt(option.value.length - 1));
        return number > highestNumber ? number : highestNumber;
      }
      return highestNumber;
    }, 0);
    return highestNumber;
  }
  return 0;
};

export const getHighestNumberInSubOptionValues = (
  options: SubOptionType[],
  t: TFunction,
) => {
  if (isThisArrayNotEmpty(options)) {
    const highestNumber = options.reduce((highestNumber, option) => {
      if (option.name.startsWith(t("newSubOption"))) {
        const number = parseInt(option.value.charAt(option.value.length - 1));
        return number > highestNumber ? number : highestNumber;
      }
      return highestNumber;
    }, 0);
    return highestNumber;
  }
  return 0;
};

export const checkForForbiddenChars = (value: string) => {
  if (value.includes("|") || value.includes("-") || value.includes(";")) {
    return false;
  }
  return true;
};

export const validateOptionValue = (
  newOption: SubOptionType | OptionType,
  subOptions: SubOptionType[] | OptionType[],
  optionValue: string,
  dispatch: Dispatch,
) => {
  if (checkForDuplacateValues(newOption, subOptions)) {
    sendToasterMessage(
      dispatch,
      "Dve možnosti nemôžu mať rovnaké hodnoty",
      MessageTypeEnum.Error,
    );
    return false;
  }
  if (!checkForForbiddenChars(optionValue)) {
    sendToasterMessage(
      dispatch,
      "Neplatný znak v hodnote - Zakázané znaky sú '-' '|' ';'",
      MessageTypeEnum.Error,
    );
    return false;
  }
  return true;
};

export const getNewOptionID = (options: OptionType[], t: TFunction) => {
  const highestNumberInOptionValues = getHighestNumberInOptionValues(
    options,
    t,
  );
  return highestNumberInOptionValues + 1;
};

export const getNewSubOptionID = (options: SubOptionType[], t: TFunction) => {
  const highestNumberInOptionValues = getHighestNumberInSubOptionValues(
    options,
    t,
  );
  return highestNumberInOptionValues + 1;
};

export const verifyPhoneNumber = (phoneNumber: string) => {
  const SK_CZ_PHONE_REGEX = /^(?:09\d{8}|\d{9}|\+421\d{9}|\+420\d{9})$/;
  const normalizedPhoneNumber = phoneNumber.replace(/\s/g, "");
  return SK_CZ_PHONE_REGEX.test(normalizedPhoneNumber);
};
